import * as React from 'react';
import { Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import { isOrderCancellable } from '@klareospace/pharos-lib-types';
import type { Order } from '@klareospace/pharos-lib-types';
import type { RowAction } from '@klareospace/pharos-lib-web-utils';
import { InlineLabel, InlineCopyText, OrderStatusBadge, RowActionsMenu } from '@klareospace/pharos-lib-web-utils';

import { ShipperAvatar } from '&/features/settings/shippers';

import { useCancelOrder } from '../hooks/use-cancel-order';
import { CancelOrderDialog } from './cancel-order-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DrawerHeaderProps {
  readonly copiedTooltip: string;
  readonly copyTooltip: string;
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DrawerHeader({ copiedTooltip, copyTooltip, order }: DrawerHeaderProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const { hasPermission } = usePermissions();
  const cancelOrder = useCancelOrder(order.id);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);

  const canCancelOrder = hasPermission('orders:cancel') && isOrderCancellable(order.status);
  const actions: RowAction[] = canCancelOrder
    ? [
        {
          className: 'text-destructive',
          icon: <Ban className="h-4 w-4" />,
          label: t('drawer.actions.cancel.menuLabel'),
          onClick: (): void => setIsCancelDialogOpen(true),
        },
      ]
    : [];

  function handleConfirmCancel(): void {
    if (cancelOrder.isPending) return;
    cancelOrder.mutate(undefined, { onSettled: (): void => setIsCancelDialogOpen(false) });
  }

  return (
    <div className="border-b border-border pb-3 pe-8" data-testid="order-drawer-header">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col space-y-1">
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            <InlineCopyText
              className="text-xl font-bold font-mono text-foreground"
              copiedTooltip={copiedTooltip}
              copyTooltip={copyTooltip}
              value={order.referenceNumber}
            >
              <span>{order.referenceNumber}</span>
            </InlineCopyText>
            <OrderStatusBadge label={t(`status.${order.status}`)} status={order.status} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <InlineLabel
              labelClassName="text-foreground"
              prefix={<ShipperAvatar logoUrl={order.shipper.logo} name={order.shipper.name} size="xs" />}
              size="xs"
            >
              {order.shipper.name}
            </InlineLabel>
            {order.externalId !== undefined && (
              <span className="text-xs text-muted-foreground font-mono">{order.externalId}</span>
            )}
          </div>
        </div>
        <RowActionsMenu actions={actions} ariaLabel={t('drawer.actions.ariaLabel')} />
      </div>
      <CancelOrderDialog
        isPending={cancelOrder.isPending}
        onConfirm={handleConfirmCancel}
        onOpenChange={setIsCancelDialogOpen}
        open={isCancelDialogOpen}
        referenceNumber={order.referenceNumber}
      />
    </div>
  );
}
